import { GlobalConfigType } from 'actions'
import { LoanStatusType, statusAppliesToChannel, userHasAccountType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { loanStatusChange } from 'services'
import { confirm, prompt } from 'utils'

export const getLoanStatusChangeErrorMessage = (
  errors: Record<string, string[]>,
  loanStatus: string,
  loanGlobalStatus: any,
) => {
  let content: any = []
  ;[
    ['structure', 'Loan Structure'],
    ['loanDetail', 'Loan Detail'],
    ['application', 'Loan Application'],
    ['submission', 'Loan Submission'],
    ['overview', 'Loan Overview'],
    ['underwriting', 'Underwriting'],
    ['tasks', 'Tasks'],
    ['budget', 'Budget & Scope of Work'],
  ].map((item) => {
    const key = item[0]
    const label = item[1]
    if (errors[key].length) {
      content.push(
        <div className="mt-1">
          <div className="border-b border-red-200 mb-1">{label}</div>
          {errors[key].map((error: string) => {
            return <div className="ml-4">- {error}</div>
          })}
        </div>,
      )
    }
  })
  const contentElement = (
    <div className="text-left bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-[14px] mb-4">
      <div className="font-semibold text-[14.5px] mb-2">
        Following Steps are required to change status to "{loanGlobalStatus[loanStatus]}"{' '}
      </div>
      {content}
    </div>
  )

  return contentElement
}

/**
 * The statuses this user may move a loan to, given the status it is in now.
 * `Loan.Status.All` sees every configured status; everyone else sees the status itself, whatever the
 * status config opens up to their account type, and Withdrawn.
 */
export const useAllowedLoanStatuses = (
  status: string,
  channel?: string | number | null,
): Record<string, string> => {
  const { data: permissionData, hasPermission } = usePermissions()
  const [loanGlobalStatus, profile, loanStatusConfig] = useSelector((state: any) => [
    state.loanGlobalStatus,
    state.auth.profile,
    state.globalConfigReducer[GlobalConfigType.LoanStatus],
  ])

  return useMemo(() => {
    const addOption = (result: Record<string, string>, key: string) => {
      if (key !== status && !statusAppliesToChannel(loanStatusConfig?.[key], channel)) return
      const label = loanGlobalStatus?.[key]
      if (typeof label === 'string' && label.trim().length > 0) result[key] = label
    }

    if (hasPermission('Loan.Status.All')) {
      const allOptions: Record<string, string> = {}
      Object.keys(loanGlobalStatus || {}).forEach((key) => addOption(allOptions, key))
      return allOptions
    }

    const rlt: Record<string, string> = {}
    addOption(rlt, status)
    if (loanStatusConfig) {
      const keys: string[] = []
      Object.keys(loanStatusConfig).map((key) => {
        if (userHasAccountType(profile, loanStatusConfig[key].toPermission)) keys.push(key)
      })
      keys.map((key) => addOption(rlt, key))
    }
    if (['withdrawn', 'funded'].indexOf(status) === -1) {
      addOption(rlt, 'withdrawn')
    }
    return rlt
  }, [permissionData, hasPermission, loanGlobalStatus, loanStatusConfig, profile?.accountType, status, channel])
}

/**
 * The loan-status change flow — per-status confirmation text / auto note, the "required steps"
 * error dialog and its override path. Shared by the status dropdown and by the pipeline Kanban
 * board's drag-and-drop, so the two can never drift apart.
 *
 * Resolves true only when the status actually changed.
 */
export const useLoanStatusChange = () => {
  const { hasPermission } = usePermissions()
  const [loanGlobalStatus, loanStatusConfig] = useSelector((state: any) => [
    state.loanGlobalStatus,
    state.globalConfigReducer[GlobalConfigType.LoanStatus],
  ])

  const changeStatus = async (loanNumber: number, value: string): Promise<boolean> => {
    if (!(Number(loanNumber) > 0)) return false
    if (value === LoanStatusType.RESTRUCTURERELEASE) {
      toast(`Please Update on 'Loan Progress Restrictions'`, { type: 'warning' })
      return false
    }

    let autoNote = ''
    const statusConf = loanStatusConfig?.[value]
    if (statusConf?.confirmRequired && statusConf?.confirmText1) {
      if (statusConf.hasAutoNote) {
        const res: any = await prompt(statusConf.confirmText1)
        if (res === false) return false
        autoNote = res
      } else {
        const confirmComponent = <div className="mb-2 whitespace-pre-wrap">{statusConf.confirmText1}</div>
        const proceed = await confirm(confirmComponent)
        if (!proceed) return false
      }
    }

    try {
      const res = await loanStatusChange(value, loanNumber, false, autoNote)
      if (res.success) return true

      const contentElement = getLoanStatusChangeErrorMessage(res.errors, value, loanGlobalStatus)
      if (hasPermission('Loan.Status.Override')) {
        const result = await confirm(contentElement, { titleYes: 'Override', titleNo: 'Close' })
        if (result) {
          const overrideRes = await loanStatusChange(value, loanNumber, true)
          if (overrideRes.success) return true
        }
      } else await confirm(contentElement, { titleYes: false, titleNo: 'Close' })
    } catch {}
    return false
  }

  return { changeStatus }
}
